// Mapeia códigos canônicos do back pra mensagens PT-BR. Sem lib i18n — escrita direto.
// Cobertura: códigos esperados em readme §16 + Onda 1 spec.
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

const MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'E-mail ou senha incorretos.',
  ACCOUNT_PENDING_APPROVAL: 'Sua conta está aguardando autorização do administrador.',
  EMAIL_TAKEN: 'Este e-mail já está cadastrado.',
  WEAK_PASSWORD: 'A senha precisa de pelo menos 6 caracteres.',
  INVITE_EXPIRED: 'O convite expirou. Peça um novo ao administrador.',
  INVITE_INVALID: 'Convite inválido.',
  GOOGLE_TOKEN_INVALID: 'Não foi possível confirmar sua conta do Google. Tente novamente.',
  GOOGLE_EMAIL_UNVERIFIED:
    'O Google não confirmou este e-mail. Verifique o endereço na sua conta Google e tente de novo.',
  GOOGLE_ACCOUNT_MISMATCH:
    'Este e-mail já está vinculado a outra conta do Google. Entre com e-mail e senha ou fale com o administrador.',
  GOOGLE_NOT_CONFIGURED: 'Login com Google não está configurado. Fale com o suporte.',
  WHATSAPP_PAIRING_FAILED: 'Não conseguimos conectar com o WhatsApp. Tente novamente.',
  WHATSAPP_NOT_PAIRED: 'Você precisa conectar o WhatsApp antes de continuar.',
  WHATSAPP_EXCHANGE_FAILED: 'Não foi possível concluir a conexão com a Meta.',
  WHATSAPP_EXCHANGE_NO_TOKEN: 'A Meta não retornou um token de acesso. Refaça a conexão.',
  WHATSAPP_SUBSCRIBE_FAILED: 'Não foi possível inscrever sua conta do WhatsApp no app.',
  WHATSAPP_APP_NOT_CONFIGURED: 'Integração com a Meta não configurada. Fale com o suporte.',
  NOTIFICAME_CHANNEL_TAKEN:
    'Este canal NotificaMe já está conectado a outra loja. Use um canal diferente ou desconecte-o da outra loja primeiro.',
  ORIGIN_PRICE_TABLE_EMPTY:
    'A empresa de origem não tem tabela de preços. Escolha outra origem, importe um CSV depois ou comece do zero.',
  INSUFFICIENT_ROLE: 'Você não tem permissão para essa ação.',
  TONE_INVALID: 'Tom da IA inválido.',
  UNAUTHORIZED: 'Sessão expirada. Faça login novamente.',
  FORBIDDEN: 'Você não tem permissão para essa ação.',
  RATE_LIMITED: 'Muitas tentativas. Aguarde alguns instantes.',
  NETWORK: 'Falha de conexão. Verifique sua internet.',
};

interface BackError {
  code?: string;
  message?: string;
  // Motivo específico (ex.: mensagem da Meta no onboarding do WhatsApp). Quando
  // presente, é anexado à frase amigável pra o usuário ver a causa acionável.
  details?: string;
}

export function parseAuthError(err: FetchBaseQueryError | SerializedError | undefined): string {
  if (!err) return 'Algo deu errado. Tente novamente.';
  if ('status' in err) {
    if (err.status === 'FETCH_ERROR' || err.status === 'TIMEOUT_ERROR') {
      return MESSAGES.NETWORK;
    }
    const body = (err.data ?? {}) as BackError;
    const friendly = body.code && MESSAGES[body.code] ? MESSAGES[body.code] : body.message;
    if (friendly) {
      return body.details ? `${friendly} ${body.details}` : friendly;
    }
    if (err.status === 401) return MESSAGES.UNAUTHORIZED;
    if (err.status === 403) return MESSAGES.FORBIDDEN;
    if (err.status === 429) return MESSAGES.RATE_LIMITED;
  }
  if ('message' in err && err.message) return err.message;
  return 'Algo deu errado. Tente novamente.';
}

/**
 * Código canônico do erro, quando o back mandou um.
 *
 * `parseAuthError` devolve a frase para o usuário; isto é para a UI DECIDIR — o caso
 * de uso é `GOOGLE_SIGNUP_REQUIRED`, que não é uma falha a ser exibida, e sim o
 * desvio para a tela de empresa.
 */
export function authErrorCode(
  err: FetchBaseQueryError | SerializedError | undefined,
): string | null {
  if (!err || !('status' in err)) return null;
  const body = (err.data ?? {}) as BackError;
  return body.code ?? null;
}
